/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import Swal from "sweetalert2/dist/sweetalert2.js";
import Pagination from "react-js-pagination";
import useSortableData from "../../hooks/useSortableData";
import { NoteState } from "../../context/notes/NoteState";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import "./EmployeeMaster.css";

const ROSTERS = [
  {
    key: "active",
    label: "Active",
    note: "Currently employed",
    status: "1",
  },
  {
    key: "resigned",
    label: "Resigned",
    note: "Employment status",
    status: "0",
    employment_status: "Resigned",
  },
  {
    key: "terminated",
    label: "Terminated",
    note: "Employment status",
    status: "0",
    employment_status: "Terminated",
  },
  {
    key: "inactive",
    label: "Inactive",
    note: "Account off, still employed",
    status: "0",
    employment_status: "InactiveAccount",
  },
];

const formatDate = (value) => {
  if (!value || value === "0000-00-00" || value === "null") return "";
  const datePart = String(value).split("T")[0].split(" ")[0];
  if (!datePart || datePart.startsWith("0000")) return "";
  const parts = datePart.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return datePart;
};

const text = (value) => {
  const cleaned = String(value ?? "").trim();
  return cleaned && cleaned !== "null" ? cleaned : "";
};

const mapStaff = (value) => ({
  id: value.id,
  uid: value.uid,
  branch_name: value.branch_name,
  first_name: value.first_name,
  last_name: value.last_name,
  father_name: value.father_name,
  department: value.department,
  designation: value.designation,
  designation_id: value.designation_id,
  email: value.email,
  official_email: value.official_email,
  phone: value.phone,
  gender: value.gender,
  dob: value.dob,
  address: value.address,
  city: value.city,
  cnic: value.cnic,
  joining: value.joining,
  image: value.image,
  employment_status: value.employment_status,
  employment_end_date: value.employment_end_date,
  status: value.status,
});

const employmentLabel = (value) => text(value) || "Not recorded";

const employmentClass = (value) => {
  const status = text(value);
  if (status === "Employed") return "employed";
  if (status === "Resigned") return "resigned";
  if (status === "Terminated") return "terminated";
  return "";
};

const accountLabel = (status) => {
  const value = String(status);
  if (value === "1") return "Account active";
  if (value === "0") return "Account inactive";
  if (value === "2") return "Account removed";
  return "";
};

const initials = (row) => {
  const first = text(row.first_name).charAt(0);
  const last = text(row.last_name).charAt(0);
  return `${first}${last}`.toUpperCase() || "—";
};

const ViewTrainer = () => {
  const { state } = useContext(NoteState);
  const { branch_id, role, UId } = state;
  const canManage = role === "1" || role === "3" || role === "12";

  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState("50");
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [roster, setRoster] = useState("active");
  const [counts, setCounts] = useState({
    active: 0,
    resigned: 0,
    terminated: 0,
    inactive: 0,
  });
  const [showLogins, setShowLogins] = useState(false);
  const [branchId, setBranchId] = useState(branch_id || "");
  const [gender, setGender] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [designationId, setDesignationId] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [nameQuery, setNameQuery] = useState("");
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const searchTimer = useRef(null);

  const currentRoster = ROSTERS.find((item) => item.key === roster) || ROSTERS[0];

  const fetchStaff = async (target, pageNo, pageLimit) => {
    const response = await UserServices.getactiveUsers(
      branchId,
      showLogins ? "1" : target.status,
      pageLimit,
      pageNo,
      gender,
      departmentId,
      showLogins ? "login" : "staff",
      designationId,
      showLogins ? undefined : target.employment_status,
      nameQuery
    );
    return {
      rows: (response.data?.data?.data || []).map(mapStaff),
      total: Number(response.data?.totalRecord || 0),
      pages: Number(response.data?.totalPages || 1),
      current: Number(response.data?.data?.current_page || pageNo),
    };
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setTableLoading(true);
      setLoadError("");
      try {
        const result = await fetchStaff(currentRoster, page, limit);
        if (cancelled) return;
        setRows(result.rows);
        setTotalResults(result.total);
        setTotalPages(result.pages);
        if (!showLogins) {
          setCounts((prev) => ({ ...prev, [currentRoster.key]: result.total }));
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setRows([]);
          setTotalResults(0);
          setTotalPages(1);
          if (!showLogins) {
            setCounts((prev) => ({ ...prev, [currentRoster.key]: 0 }));
          }
        } else {
          setRows([]);
          setLoadError("The staff list could not be loaded. Refresh the page and try again.");
        }
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    };

    const loadCounts = async () => {
      const next = {};
      await Promise.all(
        ROSTERS.map(async (item) => {
          try {
            const result = await UserServices.getactiveUsers(
              branchId,
              item.status,
              1,
              1,
              gender,
              departmentId,
              "staff",
              designationId,
              item.employment_status,
              nameQuery
            );
            next[item.key] = Number(result.data?.totalRecord || 0);
          } catch (err) {
            next[item.key] = err?.response?.status === 404 ? 0 : null;
          }
        })
      );
      if (cancelled) return;
      setCounts((prev) => {
        const merged = { ...prev };
        ROSTERS.forEach((item) => {
          if (next[item.key] !== null && next[item.key] !== undefined) {
            merged[item.key] = next[item.key];
          }
        });
        return merged;
      });
    };

    load();
    if (!showLogins) loadCounts();

    return () => {
      cancelled = true;
    };
    // fetchStaff closes over the current filters; refreshKey reloads after an action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    branchId,
    gender,
    departmentId,
    designationId,
    roster,
    nameQuery,
    page,
    limit,
    showLogins,
    refreshKey,
  ]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [departmentRes, designationRes, branchRes] = await Promise.all([
          UserServices.departmentName(),
          UserServices.designationName(),
          UserServices.branchName(),
        ]);
        setDepartments(departmentRes.data?.data || []);
        setDesignations(designationRes.data?.data || []);
        setBranches(branchRes.data?.data || []);
      } catch (err) {
        setDepartments([]);
        setDesignations([]);
        setBranches([]);
      }
    };
    loadFilters();
  }, []);

  const reload = () => setRefreshKey((value) => value + 1);

  const makeInActive = async (staffId) => {
    try {
      setLoading(true);
      const response = await UserServices.inactiveStaff(staffId);
      if (response.status === 204) {
        toast.error("Marked inactive.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          theme: "colored",
        });
        reload();
      }
    } catch (err) {
      toast.error("Could not update this record.", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  const inactiveStaff = (staffId) => {
    Swal.fire({
      title: "Mark this employee inactive?",
      text: "This switches the account off. It does not change the employment status.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#8f1d2c",
      cancelButtonColor: "#5e6772",
      confirmButtonText: "Mark inactive",
    }).then((result) => {
      if (result.isConfirmed) makeInActive(staffId);
    });
  };

  const makeActive = async (staffId) => {
    try {
      setLoading(true);
      const response = await UserServices.activeStaff(staffId);
      if (response.status === 204) {
        toast.success("Account activated.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          theme: "colored",
        });
        reload();
      }
    } catch (err) {
      toast.error("Could not update this record.", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  const activeStaff = (staffId) => {
    Swal.fire({
      title: "Activate this account?",
      text: "This turns the login account back on. Employment status stays as it is stored.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#17663a",
      cancelButtonColor: "#5e6772",
      confirmButtonText: "Activate",
    }).then((result) => {
      if (result.isConfirmed) makeActive(staffId);
    });
  };

  const deleteRecord = async (staffId) => {
    try {
      setLoading(true);
      const response = await UserServices.deleteStaff(staffId);
      if (response.status === 204) {
        toast.error("Record removed.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          theme: "colored",
        });
        reload();
      }
    } catch (err) {
      toast.error("Could not remove this record.", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  const delStaff = (staffId) => {
    Swal.fire({
      title: "Remove this record?",
      text: "This uses the existing delete action.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#8f1d2c",
      cancelButtonColor: "#5e6772",
      confirmButtonText: "Remove",
    }).then((result) => {
      if (result.isConfirmed) deleteRecord(staffId);
    });
  };

  const onSearch = (value) => {
    setNameInput(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setNameQuery(value.trim());
      setPage(1);
    }, 400);
  };

  const chooseRoster = (key) => {
    setShowLogins(false);
    setRoster(key);
    setPage(1);
  };

  const exportHref = () => {
    const params = new URLSearchParams();
    params.set("status", currentRoster.status);
    params.set("account_type", "staff");
    if (branchId) params.set("branch_id", branchId);
    if (gender) params.set("gender", gender);
    if (departmentId) params.set("department_id", departmentId);
    if (designationId) params.set("designation_id", designationId);
    if (nameQuery) params.set("name", nameQuery);
    if (currentRoster.employment_status) {
      params.set("employment_status", currentRoster.employment_status);
    }
    return `${process.env.REACT_APP_API_Link}/auth/exportusers?${params.toString()}`;
  };

  const generatePdf = async () => {
    setLoading(true);
    try {
      const result = await fetchStaff(currentRoster, 1, 1000);
      const doc = new jsPDF("l", "mm", "a4");
      doc.setFontSize(14);
      doc.text(`Employee Master — ${showLogins ? "System logins" : currentRoster.label}`, 8, 10);
      doc.autoTable({
        head: [[
          "Sr#",
          "Employee ID",
          "Name",
          "Father Name",
          "Department",
          "Position",
          "Branch",
          "Phone",
          "Email",
          "Gender",
          "Joining",
          "Employment",
          "End date",
        ]],
        body: result.rows.map((el, index) => [
          index + 1,
          text(el.uid),
          `${text(el.first_name)} ${text(el.last_name)}`.trim(),
          text(el.father_name),
          text(el.department),
          text(el.designation),
          text(el.branch_name),
          text(el.phone),
          text(el.official_email) || text(el.email),
          text(el.gender),
          formatDate(el.joining),
          employmentLabel(el.employment_status),
          formatDate(el.employment_end_date),
        ]),
        startY: 16,
        headStyles: { fillColor: [143, 29, 44], textColor: 255, fontSize: 8 },
        styles: { fontSize: 8, cellPadding: 1.4, overflow: "linebreak" },
        margin: { top: 16, left: 8, right: 8 },
        theme: "grid",
      });
      doc.save(`employee-master-${currentRoster.key}.pdf`);
    } catch (err) {
      toast.error("Could not prepare the PDF.", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="em-master">
            <header className="em-head">
              <div>
                <p className="em-kicker">Human resources</p>
                <h1>Employee Master</h1>
              </div>
              <div className="em-head-actions">
                <button type="button" className="em-btn" onClick={generatePdf}>
                  PDF
                </button>
                {!showLogins && (
                  <a className="em-btn" href={exportHref()}>
                    Excel
                  </a>
                )}
                <button
                  type="button"
                  className={`em-btn ${showLogins ? "active" : ""}`}
                  onClick={() => {
                    setShowLogins((value) => !value);
                    setPage(1);
                  }}
                >
                  {showLogins ? "Back to staff" : "System logins"}
                </button>
                <Link to="/add-staff" className="em-btn primary">
                  Add staff
                </Link>
              </div>
            </header>

            {!showLogins && (
              <div className="em-tabs">
                {ROSTERS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`em-tab em-tab-${item.key} ${roster === item.key ? "selected" : ""}`}
                    onClick={() => chooseRoster(item.key)}
                  >
                    <strong>{counts[item.key]}</strong>
                    <span>{item.label}</span>
                    <small>{item.note}</small>
                  </button>
                ))}
              </div>
            )}

            <section className="em-panel">
              <div className="em-filters">
                <div className="em-field">
                  <label>Branch</label>
                  <select
                    value={branchId}
                    onChange={(e) => {
                      setBranchId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All branches</option>
                    {branches.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="em-field">
                  <label>Search name</label>
                  <input
                    type="text"
                    value={nameInput}
                    placeholder="Employee name"
                    onChange={(e) => onSearch(e.target.value)}
                  />
                </div>
                <div className="em-field">
                  <label>Gender</label>
                  <select
                    value={gender}
                    onChange={(e) => {
                      setGender(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
                <div className="em-field">
                  <label>Department</label>
                  <select
                    value={departmentId}
                    onChange={(e) => {
                      setDepartmentId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All departments</option>
                    {departments.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="em-field">
                  <label>Position</label>
                  <select
                    value={designationId}
                    onChange={(e) => {
                      setDesignationId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All positions</option>
                    {designations.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="em-field">
                  <label>Rows</label>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="250">250</option>
                  </select>
                </div>
              </div>

              <StaffTable
                rows={rows}
                loading={tableLoading}
                error={loadError}
                page={page}
                limit={limit}
                roster={currentRoster}
                showLogins={showLogins}
                canManage={canManage}
                currentUserId={UId}
                onInactive={inactiveStaff}
                onActive={activeStaff}
                onDelete={delStaff}
              />

              <div className="em-foot">
                <p>
                  {showLogins
                    ? `${totalResults} system login${totalResults === 1 ? "" : "s"}`
                    : `${totalResults} ${currentRoster.label.toLowerCase()} record${totalResults === 1 ? "" : "s"}`}
                  {departmentId || designationId || gender || nameQuery || branchId
                    ? " matching the filters"
                    : ""}
                </p>
                {totalResults > Number(limit) && totalPages > 1 ? (
                  <Pagination
                    activePage={page}
                    itemsCountPerPage={parseInt(limit, 10)}
                    totalItemsCount={totalResults}
                    onChange={(next) => setPage(next)}
                    pageRangeDisplayed={6}
                    itemClass="page-item"
                    linkClass="page-link"
                    firstPageText="First"
                    lastPageText="Last"
                  />
                ) : null}
              </div>
            </section>
          </div>
        </div>
      </main>
      <div className={`loader ${loading ? "in" : ""}`}>
        <div className="spinner-border main-spin"></div>
      </div>
    </div>
  );
};

const StaffTable = ({
  rows,
  loading,
  error,
  page,
  limit,
  roster,
  showLogins,
  canManage,
  currentUserId,
  onInactive,
  onActive,
  onDelete,
}) => {
  const { items, requestSort, sortConfig } = useSortableData(rows || []);
  const sortClass = (key) => (sortConfig && sortConfig.key === key ? sortConfig.direction : undefined);
  const colSpan = 9;

  return (
    <div className="em-table-wrap">
      <table className="em-table">
        <thead>
          <tr>
            <th className="no-sort">#</th>
            <th onClick={() => requestSort("first_name")} className={sortClass("first_name")}>Employee</th>
            <th onClick={() => requestSort("department")} className={sortClass("department")}>Department</th>
            <th onClick={() => requestSort("designation")} className={sortClass("designation")}>Position</th>
            <th onClick={() => requestSort("branch_name")} className={sortClass("branch_name")}>Branch</th>
            <th className="no-sort">Contact</th>
            <th onClick={() => requestSort("joining")} className={sortClass("joining")}>Joining</th>
            <th onClick={() => requestSort("employment_status")} className={sortClass("employment_status")}>Employment</th>
            <th className="no-sort">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={colSpan} className="em-empty">Loading records…</td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan={colSpan} className="em-error">{error}</td>
            </tr>
          ) : items.length ? (
            items.map((el, index) => {
              const serial = (page - 1) * Number(limit) + index + 1;
              const showAccount = roster.key === "resigned" || roster.key === "terminated";
              return (
                <tr key={el.id}>
                  <td>{serial}</td>
                  <td>
                    <div className="em-person">
                      {text(el.image) ? (
                        <img className="em-photo" src={el.image} alt="" />
                      ) : (
                        <span className="em-avatar">{initials(el)}</span>
                      )}
                      <div>
                        <Link className="em-name" to={`/staff-profile/${el.id}`}>
                          {text(el.first_name)} {text(el.last_name)}
                        </Link>
                        <div className="em-sub">ID {text(el.uid) || "—"}</div>
                        {text(el.father_name) ? (
                          <div className="em-sub">{text(el.father_name)}</div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td>{text(el.department) || "—"}</td>
                  <td>{text(el.designation) || "—"}</td>
                  <td>{text(el.branch_name) || "—"}</td>
                  <td>
                    <div>{text(el.phone) || "—"}</div>
                    {(text(el.official_email) || text(el.email)) && (
                      <div className="em-sub">{text(el.official_email) || text(el.email)}</div>
                    )}
                  </td>
                  <td>{formatDate(el.joining) || "—"}</td>
                  <td>
                    <span className={`em-badge ${employmentClass(el.employment_status)}`}>
                      {employmentLabel(el.employment_status)}
                    </span>
                    {(text(el.employment_status) === "Resigned" || text(el.employment_status) === "Terminated") && formatDate(el.employment_end_date) ? (
                      <div className="em-sub">Ended {formatDate(el.employment_end_date)}</div>
                    ) : null}
                    {showAccount && <div className="em-sub">{accountLabel(el.status)}</div>}
                  </td>
                  <td>
                    <div className="em-actions">
                      <Link className="em-action" to={`/staff-profile/${el.id}`}>Profile</Link>
                      <Link className="em-action" to={`/print-staff-card/${el.id}`}>Card</Link>
                      {canManage && !showLogins && String(el.status) === "1" && String(currentUserId) !== String(el.id) && (
                        <button type="button" className="em-action danger" onClick={() => onInactive(el.id)}>
                          Inactive
                        </button>
                      )}
                      {canManage && !showLogins && String(el.status) === "0" && (
                        <>
                          <button type="button" className="em-action" onClick={() => onActive(el.id)}>
                            Activate
                          </button>
                          <button type="button" className="em-action danger" onClick={() => onDelete(el.id)}>
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={colSpan} className="em-empty">
                No records for this list and the selected filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ViewTrainer;
